import { create } from 'zustand';

export interface TaskingOrderPricing {
  model: string;
  unit: string;
  confidence: string | null;
  quotation_required: boolean;
}

export interface TaskingOrderCostEstimate {
  provider: string;
  total_credits: number;
  total_size: number;
  unit: string;
}

export interface TaskingOrder {
  id: string;
  quotation_id?: string;
  bbox: number[];
  start_date: string;
  end_date: string;
  order_type: string;
  search_mode: string;
  status: string;
  selected_item_id: string;
  up42_order_id: string | null;
  provider: string;
  cost_estimate?: TaskingOrderCostEstimate;
  pricing: TaskingOrderPricing;
  created_at: string;
  updated_at: string;
}

interface TaskingOrdersStore {
  orders: TaskingOrder[];
  selectedOrderIds: Set<string>;
  isLoading: boolean;
  isRejecting: boolean;
  isAccepting: boolean;
  error: Error | null;
  rejectError: Error | null;
  acceptError: Error | null;
  setOrders: (orders: TaskingOrder[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  setRejecting: (rejecting: boolean) => void;
  setRejectError: (error: Error | null) => void;
  setAccepting: (accepting: boolean) => void;
  setAcceptError: (error: Error | null) => void;
  removeOrder: (orderId: string) => void;
  removeOrders: (orderIds: string[]) => void;
  getReadyOrders: () => TaskingOrder[];
  getReadyCount: () => number;
  toggleOrderSelection: (orderId: string) => void;
  selectAllOrders: () => void;
  getSelectedOrders: () => TaskingOrder[];
  getTotalCredits: () => number;
  initializeSelection: () => void;
}

export const useTaskingOrdersStore = create<TaskingOrdersStore>((set, get) => ({
  orders: [],
  selectedOrderIds: new Set<string>(),
  isLoading: false,
  isRejecting: false,
  isAccepting: false,
  error: null,
  rejectError: null,
  acceptError: null,

  setOrders: (orders) => {
    console.log('[Store] Setting orders:', orders.length);
    orders.forEach((o) => {
      console.log(`[Store] Order ${o.id}: status=${o.status}, up42_order_id=${o.up42_order_id}`);
    });

    set({ orders });
    // Auto-select all ready orders when orders are loaded
    const readyOrderIds = orders
      .filter((order) => order.status === 'done' && !order.up42_order_id)
      .map((order) => order.id);
    set({ selectedOrderIds: new Set(readyOrderIds) });
  },

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setRejecting: (isRejecting) => set({ isRejecting }),
  setRejectError: (rejectError) => set({ rejectError }),
  setAccepting: (isAccepting) => set({ isAccepting }),
  setAcceptError: (acceptError) => set({ acceptError }),

  removeOrder: (orderId: string) => {
    set((state) => {
      const newOrders = state.orders.filter((order) => order.id !== orderId);
      const newSelectedIds = new Set(state.selectedOrderIds);
      newSelectedIds.delete(orderId);
      return { orders: newOrders, selectedOrderIds: newSelectedIds };
    });
  },

  removeOrders: (orderIds: string[]) => {
    set((state) => {
      const idsToRemove = new Set(orderIds);
      const newOrders = state.orders.filter((order) => !idsToRemove.has(order.id));
      const newSelectedIds = new Set(state.selectedOrderIds);
      for (const id of orderIds) {
        newSelectedIds.delete(id);
      }
      return { orders: newOrders, selectedOrderIds: newSelectedIds };
    });
  },

  getReadyOrders: () => {
    return get().orders.filter((order) => order.status === 'done' && !order.up42_order_id);
  },

  getReadyCount: () => {
    return get().orders.filter((order) => order.status === 'done' && !order.up42_order_id).length;
  },

  toggleOrderSelection: (orderId: string) => {
    set((state) => {
      const newSelectedIds = new Set(state.selectedOrderIds);
      if (newSelectedIds.has(orderId)) {
        newSelectedIds.delete(orderId);
      } else {
        newSelectedIds.add(orderId);
      }
      return { selectedOrderIds: newSelectedIds };
    });
  },

  selectAllOrders: () => {
    const readyOrders = get().getReadyOrders();
    const allIds = new Set(readyOrders.map((order) => order.id));
    set({ selectedOrderIds: allIds });
  },

  getSelectedOrders: () => {
    const { orders, selectedOrderIds } = get();
    return orders.filter((order) => selectedOrderIds.has(order.id));
  },

  getTotalCredits: () => {
    const selectedOrders = get().getSelectedOrders();
    return selectedOrders.reduce((total, order) => total + (order.cost_estimate?.total_credits ?? 0), 0);
  },

  initializeSelection: () => {
    const readyOrders = get().getReadyOrders();
    const allIds = new Set(readyOrders.map((order) => order.id));
    set({ selectedOrderIds: allIds });
  },
}));
